import { cn } from '@/lib/utils';

/**
 * AllergyChip — Atom (canonical tinted-pill pattern)
 *
 * Compact numbered allergy chip used in patient-chart headers, encounter
 * wrap-ups, and appointment row warning strips. Red is locked: allergy is
 * clinical danger (Signal DS rule — red is clinical-only). Distinct from
 * generic chips/tags — fixed numbering, fixed Red ramp.
 *
 * FHIR: maps to `AllergyIntolerance` R4 (one chip per AllergyIntolerance
 * resource). The chip itself shows `code.text` (allergen name). Severity /
 * reaction details belong in the tooltip or a popover, not the chip face.
 *
 * The token binding is the canonical "tinted-pill contrast" pattern that
 * applies across every tinted-bg status surface (BioStatusPill, StatusBadge,
 * drug-interaction rows, lab-out-of-range rows):
 *
 *   background  = colors/{ramp}-100  (red-100 here)
 *   numbered badge fill = colors/{ramp}-500  (red-500 here)
 *   foreground text     = colors/{ramp}-700  (red-700 here)
 *
 * Do NOT bind text to `text-status-error` — that token is a fill / glyph
 * shade. On a tinted background it goes invisible. See `CLAUDE.md` ↦
 * "Tinted chip / status pill pattern" and the AllergyChip DON'T-vs-DO at
 * https://signal-ds.vercel.app/#color.
 */

export interface AllergyChipProps {
  /** Allergen name — `AllergyIntolerance.code.text` */
  allergen: string;
  /** 1-indexed position in the list (used by tooltip/aria to reference "allergy 2 of 5"). */
  number: number;
  /** Render without a numbered badge — appropriate when the chip stands alone. */
  hideNumber?: boolean;
  onClick?: () => void;
  className?: string;
}

export function AllergyChip({
  allergen,
  number,
  hideNumber = false,
  onClick,
  className,
}: AllergyChipProps) {
  const baseClass = cn(
    'inline-flex items-center gap-2 rounded-full text-sm font-medium',
    'bg-red-100 text-red-700',
    hideNumber ? 'px-3 py-1' : 'py-1 pr-3 pl-1',
    className
  );
  const ariaLabel = `Allergy ${number}: ${allergen}`;
  const badge = !hideNumber && (
    <span
      aria-hidden="true"
      className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs font-semibold text-white"
    >
      {number}
    </span>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-label={ariaLabel}
        className={cn(
          baseClass,
          'cursor-pointer transition-colors hover:bg-red-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500 focus-visible:outline-offset-2'
        )}
      >
        {badge}
        {allergen}
      </button>
    );
  }
  return (
    <span aria-label={ariaLabel} className={baseClass}>
      {badge}
      {allergen}
    </span>
  );
}
